import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { 
    user, 
    userProfile, 
    isHost, 
    isAdmin, 
    isAttendee,
    isAuthenticated,
    loading,
    signOut 
  } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Define all navigation items with role requirements
  const allNavigationItems = [
    { label: 'Discover', path: '/event-discovery', icon: 'Search', public: true },
    { label: 'My Events', path: '/user-dashboard', icon: 'Calendar', roles: ['attendee'] },
    { label: 'My Tickets', path: '/my-tickets', icon: 'Ticket', roles: ['attendee'] },
    { label: 'Create Event', path: '/event-creation', icon: 'Plus', roles: ['host', 'admin'] },
    { label: 'Host Dashboard', path: '/host-dashboard', icon: 'BarChart3', roles: ['host', 'admin'] },
    { label: 'Event Management', path: '/event-management', icon: 'Settings', roles: ['host', 'admin'] },
    { label: 'Staff Management', path: '/staff-management', icon: 'Users', roles: ['host', 'admin'] },
    { label: 'Attendee Management', path: '/attendee-management', icon: 'UserCheck', roles: ['host', 'admin'] },
  ];

  // Filter navigation items based on authentication and user role
  const navigationItems = allNavigationItems?.filter(item => {
    // Show public items to everyone
    if (item?.public) return true;
    
    // Hide protected items if user is not authenticated
    if (!isAuthenticated || !userProfile) return false;
    
    // Show items based on user roles
    if (item?.roles) {
      return item?.roles?.includes(userProfile?.role);
    }
    
    return false;
  });

  const isActivePath = (path) => {
    return location?.pathname === path || location?.pathname?.startsWith(path);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo */}
        <Link to="/event-discovery" className="flex items-center space-x-2">
          <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
            <Icon name="Calendar" size={20} color="white" />
          </div>
          <span className="text-xl font-semibold text-foreground">EventGrid</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item?.icon} size={16} />
              <span>{item?.label}</span>
            </Link>
          ))}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center space-x-3">
          {isAuthenticated && userProfile ? (
            <>
              <Button variant="ghost" size="sm">
                <Icon name="Bell" size={16} />
              </Button>
              
              {/* User Profile Dropdown */}
              <div className="flex items-center space-x-3">
                <img
                  src={userProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.full_name || 'User')}&background=6366f1&color=ffffff`}
                  alt={userProfile?.full_name || 'User'}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="text-sm">
                  <p className="font-medium text-foreground">{userProfile?.full_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{userProfile?.role}</p>
                </div>
              </div>
              
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                <Icon name="LogOut" size={16} className="mr-2" />
                Sign Out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm">
                <Icon name="Bell" size={16} />
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/authentication">Sign In</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden"
          onClick={toggleMenu}
        >
          <Icon name={isMenuOpen ? 'X' : 'Menu'} size={20} />
        </Button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-background border-b border-border animate-fade-in">
          <nav className="px-6 py-4 space-y-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-smooth ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </Link>
            ))}
            
            {/* Mobile Actions */}
            <div className="pt-4 mt-4 border-t border-border space-y-2">
              {isAuthenticated && userProfile ? (
                <>
                  {/* User Profile Info */}
                  <div className="flex items-center space-x-3 px-4 py-3">
                    <img
                      src={userProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.full_name || 'User')}&background=6366f1&color=ffffff`}
                      alt={userProfile?.full_name || 'User'}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-foreground">{userProfile?.full_name}</p>
                      <p className="text-sm text-muted-foreground capitalize">{userProfile?.role}</p>
                    </div>
                  </div>
                  
                  <Button variant="ghost" size="sm" fullWidth className="justify-start">
                    <Icon name="Bell" size={16} className="mr-3" />
                    Notifications
                  </Button>
                  <Button variant="ghost" size="sm" fullWidth className="justify-start">
                    <Icon name="User" size={16} className="mr-3" />
                    Profile Settings
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    fullWidth 
                    className="justify-start"
                    onClick={handleSignOut}
                  >
                    <Icon name="LogOut" size={16} className="mr-3" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" fullWidth className="justify-start">
                    <Icon name="Bell" size={16} className="mr-3" />
                    Notifications
                  </Button>
                  <Button variant="outline" size="sm" fullWidth asChild>
                    <Link to="/authentication" onClick={() => setIsMenuOpen(false)}>
                      Sign In
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;